"use client";

import { useState, useEffect } from "react";
import Step2AddressInput from "./Step2AddressInput";
import Step2ElevatorSelect from "./Step2ElevatorSelect";
import { Button } from "@/components/ui/button";
import { useOrderStore } from "@/store/useOrderStore";
import { checkServiceArea, getServiceAreas } from "@/lib/service-area";
import type { AddressInfo } from "@/types/address";

interface Props {
  onComplete: () => void;
}

export default function Step2Address({ onComplete }: Props) {
  const { origin, destination, setOrigin, setDestination } = useOrderStore();
  const [originAreaError, setOriginAreaError] = useState<string | null>(null);
  const [destAreaError, setDestAreaError] = useState<string | null>(null);
  const [areaText, setAreaText] = useState('');

  useEffect(() => {
    getServiceAreas().then((areas) => {
      if (areas.length === 0) return;
      const districts = areas.map((a) => a.district.replace('구', '')).join(', ');
      setAreaText(`현재는 서울시 ${areas.length}개 구(${districts})에서만 서비스를 제공하고 있습니다. 빠르게 서비스 지역을 확장할 수 있도록 하겠습니다.`);
    });
  }, []);

  // 기존 AddressInfo를 새로운 AddressInfo 형식으로 변환
  const convertToAddressInfo = (info: typeof origin): AddressInfo | null => {
    if (!info) return null;
    return {
      address: info.address,
      addressDetail: info.addressDetail,
      zonecode: (info as AddressInfo).zonecode || "",
      roadAddress: (info as AddressInfo).roadAddress || info.address,
      jibunAddress: (info as AddressInfo).jibunAddress || "",
      buildingName: (info as AddressInfo).buildingName || "",
      lat: info.lat,
      lng: info.lng,
      hasElevator: info.hasElevator,
      floor: info.floor,
    };
  };

  const handleOriginChange = async (info: AddressInfo) => {
    const result = await checkServiceArea(info.address);
    if (!result.available) {
      setOriginAreaError(`죄송합니다. ${result.city} ${result.district}는 현재 서비스를 제공하지 않고 있습니다. 최대한 빠르게 서비스를 제공하도록 노력하겠습니다.`);
    } else {
      setOriginAreaError(null);
    }
    const update = {
      address: info.address,
      addressDetail: info.addressDetail,
      lat: info.lat || 0,
      lng: info.lng || 0,
      hasElevator: info.hasElevator,
      floor: info.floor,
    };
    // 상세주소 첫 글자가 1~5이면 엘리베이터 없음 시 층수 자동 선택
    if (!update.hasElevator && info.addressDetail) {
      const firstChar = info.addressDetail.charAt(0);
      if (firstChar >= '1' && firstChar <= '5') {
        update.floor = Number(firstChar);
      }
    }
    setOrigin(update);
  };

  const handleDestinationChange = async (info: AddressInfo) => {
    const result = await checkServiceArea(info.address);
    if (!result.available) {
      setDestAreaError(`죄송합니다. ${result.city} ${result.district}는 현재 서비스를 제공하지 않고 있습니다. 최대한 빠르게 서비스를 제공하도록 노력하겠습니다.`);
    } else {
      setDestAreaError(null);
    }
    const update = {
      address: info.address,
      addressDetail: info.addressDetail,
      lat: info.lat || 0,
      lng: info.lng || 0,
      hasElevator: info.hasElevator,
      floor: info.floor,
    };
    if (!update.hasElevator && info.addressDetail) {
      const firstChar = info.addressDetail.charAt(0);
      if (firstChar >= '1' && firstChar <= '5') {
        update.floor = Number(firstChar);
      }
    }
    setDestination(update);
  };

  const originInfo = convertToAddressInfo(origin);
  const destInfo = convertToAddressInfo(destination);

  // 엘리베이터 없음 선택 시 층수 필수 (-99 = 미선택)
  const originFloorValid = origin?.hasElevator !== false || (origin?.floor !== undefined && origin.floor !== -99);
  const destFloorValid = destination?.hasElevator !== false || (destination?.floor !== undefined && destination.floor !== -99);

  const isComplete =
    origin?.address &&
    origin?.addressDetail &&
    origin?.lat &&
    origin?.lng &&
    originFloorValid &&
    !originAreaError &&
    destination?.address &&
    destination?.addressDetail &&
    destination?.lat &&
    destination?.lng &&
    destFloorValid &&
    !destAreaError;

  return (
    <div className="space-y-6">
      <div>
        {areaText && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
            {areaText}
          </div>
        )}
      </div>
      <div>
        <p className="text-sm text-gray-500">주소를 입력하고 엘리베이터 여부를 선택해주세요</p>
      </div>

      {/* 출발지 */}
      <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-3 h-3 rounded-full bg-orange-500" />
          <span className="font-semibold text-[#E8723A]">출발지</span>
        </div>

        <Step2AddressInput
          label=""
          color="#E8723A"
          value={originInfo}
          onChange={handleOriginChange}
        />

        {originAreaError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-700">{originAreaError}</p>
          </div>
        )}

        {origin?.address && !originAreaError && (
          <Step2ElevatorSelect
            hasElevator={origin.hasElevator}
            floor={origin.floor}
            carryHelp={origin.carryHelp}
            floorPlaceholder="물품을 수거할 층을 선택해주세요"
            onElevatorChange={(has) => {
              let floor = has ? 1 : -99;
              if (!has && origin.addressDetail) {
                const fc = origin.addressDetail.charAt(0);
                if (fc >= '1' && fc <= '5') floor = Number(fc);
              }
              setOrigin({ ...origin, hasElevator: has, floor });
            }}
            onFloorChange={(f) => setOrigin({ ...origin, floor: f })}
            onCarryHelpChange={(help) => setOrigin({ ...origin, carryHelp: help })}
          />
        )}
      </div>

      {/* 구분선 */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-gray-400 text-lg">↓</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      {/* 도착지 */}
      <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-3 h-3 rounded-full bg-[#1A3A5C]" />
          <span className="font-semibold text-[#1A3A5C]">도착지</span>
        </div>

        <Step2AddressInput
          label=""
          color="#1A3A5C"
          value={destInfo}
          onChange={handleDestinationChange}
        />

        {destAreaError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-700">{destAreaError}</p>
          </div>
        )}

        {destination?.address && !destAreaError && (
          <Step2ElevatorSelect
            hasElevator={destination.hasElevator}
            floor={destination.floor}
            carryHelp={destination.carryHelp}
            floorPlaceholder="물품을 배송할 층을 선택해주세요"
            onElevatorChange={(has) => {
              let floor = has ? 1 : -99;
              if (!has && destination.addressDetail) {
                const fc = destination.addressDetail.charAt(0);
                if (fc >= '1' && fc <= '5') floor = Number(fc);
              }
              setDestination({ ...destination, hasElevator: has, floor });
            }}
            onFloorChange={(f) =>
              setDestination({ ...destination, floor: f })
            }
            onCarryHelpChange={(help) => setDestination({ ...destination, carryHelp: help })}
          />
        )}
      </div>

      {/* 완료 상태 표시 */}
      {isComplete && (
        <div className="p-3 bg-green-50 rounded-lg text-center">
          <p className="text-sm text-green-700 font-medium">
            ✅ 출발지 · 도착지 입력 완료
          </p>
        </div>
      )}

      <Button onClick={onComplete} disabled={!isComplete} className="w-full" size="lg">
        다음 단계로
      </Button>
    </div>
  );
}
